﻿import React, { Fragment, useState, useEffect } from 'react';
import { FormTextInput } from "components/form-fields";
import { useValidation } from "hooks/useValidation";
import PropTypes from 'prop-types';
import { ShowHide } from 'components/layout';
import { Button, ButtonSave } from 'components/buttons';
import { Intent } from '@blueprintjs/core';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { UserService } from 'services';
import classNames from "classnames";
import { useSelector } from 'react-redux';
import 'components/settings/settings.css';

export function UserBasicSettings(
    {
        saving = false,
        editing = false,
        ssoAccount = false,
        externalUser = false,
        inputRef,
        loading,
        onSettingsUpdate,
        onValidationUpdate,
        pending,
        pendingEmailAddress,
        user
    }
) {
    const [isValid, errors, validate] = useValidation();
    const [loadingValue, setLoading] = useState(true);
    const [updateEmail, setUpdateEmail] = useState(false);
    const [userSettings, setUserSettings] = useState({ emailAddress: "", firstName: null, lastName: null, pendingEmailAddress: "", phoneNumber: null, secondaryPhoneNumber: null });
    const [pendingEmailAddressValue, setPendingEmailAddress] = useState("");
    const [canUpdateEmail, setCanUpdateEmail] = useState(false);
    const loggedInUserId = useSelector(state => state.loggedInUser.userId);
    const [savingUpdateEmailChange, setSavingUpdateEmailChange] = useState(false);

    useEffect(onValidationChange, [isValid])

    function onValidationChange() {
        onValidationUpdate(isValid);
    }

    useEffect(function () {
        setPendingEmailAddress(pendingEmailAddress);
    }, [pendingEmailAddress])

    useEffect(() => {
        setUserSettings(user);
        if (user.userId && user.userId !== loggedInUserId && user.status !== "Pending") {
            setCanUpdateEmail(true);
        }
        
    }, [user]);

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    function onEmailChange(event) {

        if (updateEmail) {
            setPendingEmailAddress(event.target.value);
        } else {
            var updatedUser = {
                ...userSettings,
                emailAddress: event.target.value
            }

            setUserSettings(updatedUser);
            onSettingsUpdate(updatedUser);
        }
    }

    function onFirstNameChange(event) {

        var updatedUser = {
            ...userSettings,
            firstName: event.target.value
        }

        setUserSettings(updatedUser);
        onSettingsUpdate(updatedUser);
    }

    function onLastNameChange(event) {

        var updatedUser = {
            ...userSettings,
            lastName: event.target.value
        }

        setUserSettings(updatedUser);
        onSettingsUpdate(updatedUser);
    }

    function onPhoneNumberChange(event) {

        var updatedUser = {
            ...userSettings,
            phoneNumber: event.target.value,
            secondaryPhoneNumber: event.target.value === '' ? '' : userSettings.secondaryPhoneNumber
        }

        setUserSettings(updatedUser);
        onSettingsUpdate(updatedUser);
    }

    function onSecondaryPhoneNumberChange(event) {

        var updatedUser = {
            ...userSettings,
            secondaryPhoneNumber: event.target.value
        }

        setUserSettings(updatedUser);
        onSettingsUpdate(updatedUser);
    }

    useEffect(updateValidationRules, [pendingEmailAddressValue, updateEmail, user])

    function updateValidationRules() {
        let rules = [];

        if (!editing || updateEmail || externalUser) {
            rules.push({ fieldName: "emailAddress", required: true, type: "noneLogisticsEmail" });
        }

        if (externalUser) {
            rules.push({ fieldName: "firstName", required: true, maxLength: 50 });
            rules.push({ fieldName: "lastName", required: true, maxLength: 50 });
        }

        if (user?.phoneNumber !== undefined && user?.phoneNumber !== '') {
            rules.push({ fieldName: "phoneNumber", required: false, type: "phone" });
        }

        if (user?.secondaryPhoneNumber !== undefined && user?.secondaryPhoneNumber !== '') {
            rules.push({ fieldName: "secondaryPhoneNumber", required: false, type: "phone" });
        }

        if (!editing || externalUser)
        {
            validate(rules, user);
        }
        else
        {
            var emailUpdateObject = {
                emailAddress: pendingEmailAddressValue,
                phoneNumber: user.phoneNumber,
                secondaryPhoneNumber: user.secondaryPhoneNumber
            };

            validate(rules, emailUpdateObject);
        }
    }

    function OnUpdateEmail() {
        if (updateEmail) {
            setUserSettings({
                ...userSettings,
                pendingEmailAddress: ""
            });
        }

        setUpdateEmail(!updateEmail);

    }

    function OnUpdateEmailCancel() {

        setSavingUpdateEmailChange(true);

        UserService.updateEmailAddress(userSettings.userId, { emailAddress: null }).then(() => {
            setSavingUpdateEmailChange(false);
            NotificationToaster.show(Intent.SUCCESS, "Email address change was successfully cancelled.");
            setPendingEmailAddress("");
            setUpdateEmail(false);
            var updatedUser = {
                ...userSettings,
                pendingEmailAddress: ""
            }

            onSettingsUpdate(updatedUser);

        }, (error) => {
            setSavingUpdateEmailChange(false);
            NotificationToaster.show(Intent.DANGER, error);
        });
    }

    function OnUpdateEmailConfirm(){

        if (!isValid) {
            NotificationToaster.show(Intent.WARNING, "Settings not valid. Please check your settings and try again.");
            return;
        }

        setLoading(true);

        UserService.updateEmailAddress(userSettings.userId, { emailAddress: pendingEmailAddressValue }).then(() => {
            setLoading(false);
            var updatedUser = {
                ...userSettings,
                pendingEmailAddress: pendingEmailAddressValue
            }

            onSettingsUpdate(updatedUser);
            NotificationToaster.show(Intent.SUCCESS, "An email has been sent to the user to confirm the email address");
        }, (error) => {
            setLoading(false);
            NotificationToaster.show(Intent.DANGER, error);
        });
    }

    return (
        <Fragment>
            <div>

                <ShowHide
                    evaluator={userSettings.pendingEmailAddress !== undefined && userSettings.pendingEmailAddress != null && userSettings.pendingEmailAddress !== ""}
                    show={(
                        <Fragment>

                            <FormTextInput
                                large
                                id="user-email-address"
                                value={userSettings.emailAddress}
                                onChange={() => { }}
                                disabled
                                headingText="Current Email Address:"
                            />
                            <h4 className='spacer-bottom-medium'>Pending Email Address:</h4>
                            <div className="inline-items spacer-bottom">
                                <FormTextInput
                                    large
                                    id="user-email-address"
                                    value={userSettings.pendingEmailAddress}
                                    onChange={() => { }}
                                    disabled
                                />
                                <ButtonSave
                                    text="Cancel change"
                                    intent="danger"
                                    icon="cross"
                                    onClick={OnUpdateEmailCancel}
                                    disabled={savingUpdateEmailChange}
                                    loading={loadingValue}
                                ></ButtonSave>
                            </div>

                            <NotificationInline
                                show
                                text="The above user's email address is pending confirmation, once the user has confirmed the change they will be able to use it for login"
                                intent="info"
                                allowClose={false}
                            ></NotificationInline>

                        </Fragment>
                    )}
                    hide={(
                        <Fragment>
                            <NotificationInline
                                show={externalUser && pending && !loadingValue}
                                text="This external contact needs to verify their email address before changes can be made."
                                intent="info"
                                allowClose={false}
                            ></NotificationInline>

                            <h4 className='spacer-bottom-medium'>Email Address:</h4>
                            <div className={classNames("inline-items align-items-flex-start", { "spacer-bottom": !errors.emailAddress })}>

                                <FormTextInput
                                    large
                                    id="user-email-address"
                                    value={updateEmail ? pendingEmailAddressValue : userSettings.emailAddress}
                                    onChange={onEmailChange}
                                    disabled={(user.userId && (!canUpdateEmail || loadingValue || saving || ssoAccount || user.userType === "SSO" || !updateEmail))
                                        || (externalUser && (pending || saving))}
                                    loading={loadingValue}
                                    dangerHelperText={errors.emailAddress}
                                    inputRef={inputRef}>
                                </FormTextInput>
                                <ShowHide evaluator={user.userId !== undefined && user.userId && canUpdateEmail && !loadingValue && user.userType !== "SSO" && user.userType !== "External"}
                                    show={(
                                        <div>
                                            <Button
                                                text={ !updateEmail ? "Change Email" : "Cancel"} 
                                                intent="primary"
                                                onClick={OnUpdateEmail}
                                                disabled={saving || loadingValue}
                                                loading={loadingValue}
                                            ></Button>
                                        </div>
                                    )}
                                />
                            </div>

                            <ShowHide evaluator={updateEmail && user.userId}
                                show={(
                                    <div className="spacer-bottom">
                                        <NotificationInline intent="info" show allowClose={false} text="When a user's email is updated, they will need to validate their email address before they can use it for login" />

                                        <ButtonSave
                                            text="Update Email Address"
                                            intent="primary"
                                            onClick={OnUpdateEmailConfirm}
                                            disabled={saving || loadingValue || !updateEmail || ssoAccount || savingUpdateEmailChange}
                                            simpleDisabled={!isValid}
                                        ></ButtonSave>
                                    </div>
                                )}
                            />
                        </Fragment>
                    )}
                />

                
            </div>
            <FormTextInput
                large
                id="user-first-name"
                loading={loadingValue}
                value={userSettings.firstName}
                headingText="First Name:"
                onChange={onFirstNameChange}
                disabled={saving || pending || ssoAccount}
                dangerHelperText={errors.firstName} >
            </FormTextInput>
            <FormTextInput
                large
                id="user-last-name"
                loading={loadingValue}
                value={userSettings.lastName}
                headingText="Last Name:"
                onChange={onLastNameChange}
                disabled={saving || pending || ssoAccount}
                dangerHelperText={errors.lastName} >
            </FormTextInput>
            <FormTextInput
                large
                id="phone-number"
                loading={loadingValue}
                value={userSettings.phoneNumber}
                headingText="Phone Number:"
                onChange={onPhoneNumberChange}
                disabled={saving || pending || ssoAccount}
                dangerHelperText={errors.phoneNumber} >
            </FormTextInput>
            <FormTextInput
                large
                id="secondary-phone-number"
                loading={loadingValue}
                value={userSettings.secondaryPhoneNumber}
                headingText="Secondary Phone Number:"
                onChange={onSecondaryPhoneNumberChange}
                disabled={saving || pending || ssoAccount || userSettings.phoneNumber === null || userSettings.phoneNumber === ''}
                dangerHelperText={errors.secondaryPhoneNumber} >
            </FormTextInput>
        </Fragment>
    );
}

UserBasicSettings.propTypes = {
    user: PropTypes.object.isRequired,
    saving: PropTypes.bool,
    editing: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    ssoAccount: PropTypes.bool,
    externalUser: PropTypes.bool,
    inputRef: PropTypes.object
};